import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/layout/Footer';
import BusinessOverview from '../components/landing/BusinessOverview';
import PlatformShowcase from '../components/landing/PlatformShowcase';
import PuppyDogPlayer from '../components/landing/PuppyDogPlayer';


// Feature card data
const features = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Smart Portfolio Dashboard',
    description: 'Track your entire property empire with real-time valuations, equity tracking, and comprehensive performance metrics.'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'Investment Strategy Planner',
    description: 'Build a personalized property roadmap based on your goals, borrowing capacity, and risk appetite.'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    title: 'AI Document Extraction',
    description: 'Upload contracts, settlement statements, or valuations. Our AI automatically extracts price, fees, and key data for you.'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m.599-2.201a3.73 3.73 0 01-3.099 0M12 16h3m4-4a8 8 0 11-16 0 8 8 0 0116 0z" />
      </svg>
    ),
    title: 'Total Net Worth Hub',
    description: 'Track your entire financial life beyond real estate. Manage cash, stocks, crypto, and precious assets in one place.'
  }
];

// Platform capabilities
const capabilities = [
  {
    icon: '📊',
    title: 'Smart Portfolio Tracking',
    description: 'Visualize your entire property empire in one dashboard. See equity growth, rental yields, and portfolio performance at a glance.',
  },
  {
    icon: '🤖',
    title: 'AI Data Extraction',
    description: 'Our advanced AI reads your property documents and automatically fills in figures, reducing manual entry and errors.',
  },
  {
    icon: '🗺️',
    title: 'Investment Roadmaps',
    description: 'Build long-term property strategies with our scenario planner. See how each move impacts your future wealth.',
  },
  {
    icon: '📧',
    title: 'Email-to-Inbox Documents',
    description: 'Forward statements and invoices to your unique email address. We scan, extract data, and organize them for you.',
  },
  {
    icon: '💎',
    title: 'Total Wealth Tracking',
    description: 'Manage other assets like stocks, super, and custom collectibles. See your full net worth in real-time.',
  },
  {
    icon: '📈',
    title: 'Tax-Ready Reports',
    description: 'One-click generation of depreciation schedules and income statements formatted for your accountant.',
  },
  {
    icon: '💬',
    title: 'Investor Community',
    description: 'Connect with fellow property investors and share strategies in our moderated Australian community forum.',
  }
];

// Security features
const securityFeatures = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    title: 'Secure Data Storage',
    description: 'Your data is encrypted at rest and in transit'
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Multi-Factor Authentication',
    description: 'Extra layer of security for your account'
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    title: 'Protected Data Transfer',
    description: 'Secure protocols for all data exchanges'
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    title: 'Bank-Grade Security',
    description: 'Enterprise-level protection for your data'
  }
];

export const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen min-h-[100dvh] bg-white">
      <Helmet>
        <title>CanvasEstate - Smart Property Investment Portfolio Manager</title>
        <meta name="description" content="Manage your entire Australian property portfolio in one place. Real-time valuations, tax reports, automated rent tracking, and market insights." />
        <link rel="canonical" href={`https://${window.location.hostname}/`} />
        <meta property="og:title" content="CanvasEstate - Smart Property Investment Portfolio Manager" />
        <meta property="og:description" content="Manage your entire Australian property portfolio in one place. Real-time valuations, tax reports, automated rent tracking, and market insights." />
        <meta property="og:url" content={`https://${window.location.hostname}/`} />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/logo.jpg" alt="CanvasEstate" className="h-10 w-auto rounded-lg" loading="lazy" />
              <span className="text-xl font-bold text-brand-secondary hidden xs:inline">CanvasEstate</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/pricing"
                className="text-sm font-medium text-gray-700 hover:text-brand-primary transition-colors hidden md:block"
              >
                Pricing
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-gray-700 hover:text-brand-primary transition-colors hidden sm:block"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center px-3 py-2 sm:px-4 text-sm font-medium text-white bg-brand-primary hover:bg-blue-700 rounded-lg transition-colors shadow-sm whitespace-nowrap"
              >
                Get Started
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-blue-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo Badge */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-primary/20 blur-3xl rounded-full"></div>
                <img src="/logo.jpg" alt="CanvasEstate" className="relative h-24 w-auto rounded-2xl shadow-xl" loading="lazy" />
              </div>
            </div>

            {/* Main Headline */}

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-secondary mb-6 leading-tight">
              Where Smart Data Meets
              <span className="block text-brand-primary">Smarter Property Decisions</span>
            </h1>

            {/* Tagline */}
            <p className="text-lg sm:text-xl text-gray-600 mb-4 font-medium text-brand-accent uppercase tracking-wider">
              Let's Canvas Your Estate
            </p>

            {/* Description */}
            <div className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto space-y-4">
              <p>
                Manage property income, expenses, loans and tax reports with ease.
                Streamline your property accounting and get real-time insights into your portfolio.
              </p>
              <div className="inline-block bg-green-50 border border-green-200 rounded-lg py-2 px-4 shadow-sm">
                <p className="text-green-800 font-semibold mb-1">
                  🎉 Free forever for up to 10 properties
                </p>
                <p className="text-sm text-green-700 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No credit card required
                  <span className="mx-1">&bull;</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No hidden fees
                  <span className="mx-1">&bull;</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No tricks
                </p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-brand-primary hover:bg-blue-700 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                Get Started Free
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5 m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#features"
                className="inline-flex items-center px-8 py-4 text-lg font-semibold text-brand-secondary bg-white hover:bg-gray-50 rounded-xl transition-all shadow-md border border-gray-200"
              >
                Learn More
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Platform Stats */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-1">100+</div>
              <div className="text-sm text-gray-500 font-medium uppercase tracking-wider">Bank Connections</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-1">10k+</div>
              <div className="text-sm text-gray-500 font-medium uppercase tracking-wider">Property Data Points</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-1">99.9%</div>
              <div className="text-sm text-gray-500 font-medium uppercase tracking-wider">System Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-1">~0.8s</div>
              <div className="text-sm text-gray-500 font-medium uppercase tracking-wider">AI Processing Speed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Tour */}
      <PuppyDogPlayer />


      {/* 3. Business Overview (New) */}
      <BusinessOverview />

      {/* 4. Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-4">
              Optimized For Property Management
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed to save time, reduce stress, and maximize your property investment returns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-gray-50 rounded-2xl hover:bg-brand-primary hover:text-white transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-brand-primary/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center text-brand-primary group-hover:text-white mb-4 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 group-hover:text-blue-100">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Platform Showcase (New Visual Proof) */}
      <PlatformShowcase />

      {/* 6. Platform Capabilities Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-4">
              The Complete Property Intelligence Hub
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From acquisition to final analysis, everything you need to build and manage a high-performing property portfolio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="relative p-6 bg-white rounded-2xl border border-gray-200 hover:border-brand-primary transition-all hover:shadow-lg group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {capability.icon}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {capability.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Security Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Enterprise-Grade Protection
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Powered by Google Cloud infrastructure. Your financial data is protected by the same technology trusted by millions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
              >
                <div className="w-12 h-12 bg-brand-accent rounded-lg flex items-center justify-center text-brand-secondary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-brand-primary to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Take Control of Your Portfolio?
          </h2>
          <p className="text-xl text-blue-100 mb-2">
            Join property investors who've simplified their finances and reclaimed hours every month.
          </p>
          <p className="text-lg text-white font-medium mb-10 bg-brand-primary/20 inline-block px-4 py-2 rounded-lg border border-white/20">
            Free forever for up to 10 properties • No credit card required
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 text-lg font-semibold text-brand-primary bg-white hover:bg-gray-100 rounded-xl transition-all shadow-lg hover:shadow-xl"
            >
              Get Started Now
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
